import { readFileSync, existsSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import chalk from "chalk";
import type { CaiConfig } from "../types.js";
import { getGit } from "../git.js";
import { writeIfChanged } from "../utils/fs.js";

export type TaskType =
  | "api-endpoint"
  | "new-feature"
  | "refactor"
  | "data-model"
  | "auth-flow"
  | "config-change"
  | "test-coverage"
  | "general-change";

interface CaptureResult {
  taskType: TaskType;
  patternPath: string;
  changedFiles: string[];
}

export async function runPatternCapture(config: CaiConfig): Promise<CaptureResult> {
  const git = getGit(config.projectRoot);

  // Get last commit diff
  let changedFiles: string[] = [];
  let diffStat = "";
  let diffContent = "";
  try {
    const diffFiles = await git.diff(["HEAD~1", "HEAD", "--name-only"]);
    changedFiles = diffFiles.split("\n").map((f) => f.trim()).filter(Boolean);
    // --stat for cheap type-detection; limited unified diff only for the draft template
    diffStat = await git.diff(["HEAD~1", "HEAD", "--stat"]);
    diffContent = await git.diff(["HEAD~1", "HEAD", "--unified=3"]);
  } catch {
    // Might be first commit — try git show instead
    try {
      const show = await git.show(["--name-only", "--format=", "HEAD"]);
      changedFiles = show.split("\n").map((f) => f.trim()).filter(Boolean);
      diffStat = await git.show(["--stat", "--format=", "HEAD"]);
      diffContent = await git.show(["--unified=3", "HEAD"]);
    } catch (err) {
      throw new Error(
        `Cannot read git history — ensure you are in a git repository with at least one commit.\n${(err as Error).message}`
      );
    }
  }

  if (changedFiles.length === 0) {
    throw new Error("No changed files found in the last commit.");
  }

  const taskType = detectTaskType(changedFiles, diffStat);
  const today = new Date().toISOString().split("T")[0];
  const filename = `suggested-${taskType}-${today}.md`;

  const patternsDir = join(config.scaffoldRoot, "patterns");
  if (!existsSync(patternsDir)) mkdirSync(patternsDir, { recursive: true });
  const patternPath = join(patternsDir, filename);

  const content = buildPatternDraft(taskType, changedFiles, diffStat, diffContent, today);
  writeIfChanged(patternPath, content);

  // Append DRAFT entry to INDEX.md
  const indexPath = join(patternsDir, "INDEX.md");
  if (existsSync(indexPath)) {
    const indexContent = readFileSync(indexPath, "utf-8");
    if (!indexContent.includes(filename)) {
      const entry = `| [${filename}](${filename}) | [DRAFT] Auto-captured: ${taskType} |\n`;
      writeIfChanged(indexPath, indexContent.trimEnd() + "\n" + entry + "\n");
    }
  }

  return { taskType, patternPath, changedFiles };
}

export function detectTaskType(files: string[], diff: string): TaskType {
  const f = files.join(" ").toLowerCase();
  const d = diff.toLowerCase();

  if (/auth|login|logout|session|token|jwt|oauth|password/.test(f)) return "auth-flow";
  if (/route|controller|handler|endpoint|api/.test(f) && /async.*req.*res|router\.(get|post|put|delete|patch)|\[.*\].*\(req/.test(d)) return "api-endpoint";
  if (/model|schema|entity|migration|prisma/.test(f)) return "data-model";
  if (/test|spec|\.test\.|\.spec\./.test(f)) return "test-coverage";
  if (/config|\.env|settings|dockerfile|\.yaml|\.yml/.test(f)) return "config-change";

  const newFiles = files.filter((f) => !f.includes("test")).length;
  const diffLines = diff.split("\n");
  const additions = diffLines.filter((l) => l.startsWith("+")).length;
  const deletions = diffLines.filter((l) => l.startsWith("-")).length;

  if (deletions > additions * 2) return "refactor";
  if (newFiles >= 2) return "new-feature";

  return "general-change";
}

function buildPatternDraft(
  taskType: TaskType,
  changedFiles: string[],
  _diffStat: string,
  diffContent: string,
  today: string
): string {
  const diffLines = diffContent.split("\n");
  const hunks = diffLines
    .filter((l) => l.startsWith("diff --git") || l.startsWith("@@"))
    .slice(0, 10)
    .join("\n");

  const fileList = changedFiles.slice(0, 10).map((f) => `- ${f}`).join("\n");

  return `---
name: ${taskType}
description: [TODO: one line describing when to use this pattern]
triggers:
  - "${taskType.replace(/-/g, " ")}"
last_updated: ${today}
---

# ${toTitle(taskType)}

> **DRAFT** — Auto-captured from commit. Review and complete before using.

## Context
[TODO: What context should be loaded before starting this task type?]

## Steps

The following files were changed in the captured commit:

${fileList}

Diff summary:
\`\`\`
${hunks}
\`\`\`

[TODO: Turn the above into numbered steps that describe the workflow]

## Gotchas
[TODO: What went wrong or could go wrong? Add from experience.]

## Verify
- [ ] [TODO: What to check after completing this task type]

## After This Task
- [ ] Update \`.cai/ROUTER.md\` "Current Project State" if significant
- [ ] Update any \`.cai/context/\` files that are now out of date
`;
}

function toTitle(s: string): string {
  return s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function printPatternCaptureResult(result: CaptureResult): void {
  console.log();
  console.log(chalk.bold("  cai pattern capture"));
  console.log();
  console.log(chalk.dim(`  Task type detected: `) + chalk.cyan(result.taskType));
  console.log(chalk.dim(`  Files analyzed: ${result.changedFiles.length}`));
  console.log();
  console.log(chalk.green("  ✓ Draft pattern saved:"));
  console.log(chalk.dim(`    ${result.patternPath}`));
  console.log();
  console.log(chalk.dim("  Review the draft, fill in the TODOs, then run:"));
  console.log(chalk.dim("    cai check   to verify the pattern is indexed correctly"));
  console.log();
}
